import _ from "underscore";
import moment from "moment";
import find from "lodash/find";
import get from "lodash/get";
import sortBy from "lodash/sortBy";
import { getDayNumber } from "../utils/date";
import sentry from "../utils/sentry";
import actions from "./actions";
import apiUrls from "../urls";
import { setInputField } from "./fieldInputActions";
import "whatwg-fetch";

export function setApiTokens(auth0) {
  return { type: actions.SET_API_TOKENS, payload: { auth0 } };
}

async function _getUser(dispatch, getState) {
  const state = getState();

  const user = await state.api.getUser();

  if (isUserUnavailable(user)) {
    dispatch({ type: actions.USER_UNAVAILABLE });

    return null;
  }

  const prefs = user.preferences;

  dispatch({ type: actions.SET_USER, payload: user });
  dispatch({ type: actions.SET_USER_PREFERENCES, preferences: prefs });

  if (prefs && Array.isArray(prefs.workingHours)) {
    const today = moment();
    const dayNumber = getDayNumber(today);
    const workingHours = prefs.workingHours[dayNumber].workingHours;

    dispatch({ type: actions.SET_DURATION, payload: workingHours });
  }

  return user;
}

async function _getFavorites(dispatch, getState) {
  let serverFavorites;

  try {
    serverFavorites = await getState().api.getUserFavorites();
  } catch (err) {
    console.error(err);
  }

  if (serverFavorites && serverFavorites.message && serverFavorites.message === "Unauthorized") {
    throw new Error("Unauthorized")
  }

  const favorites = serverFavorites ? serverFavorites : { accounts: [], productGroups: [], opportunities: [] };

  dispatch({
    type: actions.FETCH_USER_FAVORITES_FULFILLED,
    payload: {
      ...favorites,
      accounts: _attachAccountHelpers(
        favorites.accounts,
        getState
      ),
      productGroups: _attachProductGroupHelpers(
        favorites.productGroups,
        getState
      ),
      opportunities: _attachOpportunityHelpers(
        favorites.opportunities,
        getState
      )
    }
  });
};

async function _getRoles(dispatch, getState) {
  const state = getState();

  const { api, user } = state;

  const serverRoles = await api.getRoles();

  if (!serverRoles) {
    throw new Error(`Error fetching roles`);
  }

  const roles = sortBy(serverRoles, "id");

  dispatch({ type: actions.SET_ROLES, roles });

  const ttRole = user.timeTrackingRole;
  const userRole = find(roles, { id: ttRole });
  const prefs = user.preferences;

  if (userRole) {
    dispatch({ type: actions.SET_USER_ROLE, role: userRole });
  }

  // Set default working hours based on role
  if (!prefs || !Array.isArray(prefs.workingHours)) {
    const days = [];

    let workingHours = get(userRole, "defaultWorkingHours", 8)

    if (typeof workingHours === "number") {
      workingHours = workingHours.toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    }

    for (let i = 0; i < 7; i++){
      days.push({
        enabled: i < 5,
        workingHours,
      });
    }

    dispatch({ type: actions.SET_WORKING_HOURS, days });

    dispatch({ type: actions.SET_DURATION, payload: workingHours });
  }
}

async function _getCategories(dispatch, getState) {
  const state = getState();

  const timeTrackingRole = get(state, "user.timeTrackingRole");

  const categories = await state.api.getCategories(timeTrackingRole);

  dispatch({ type: actions.FETCH_CATEGORIES_FULFILLED, payload: categories });

  // if we have only one category, select it for the user automatically
  if (categories.length === 1) {
    dispatch(setInputField("CATEGORY", categories[0]));
  }
}

async function _fetchMostRecentEntry(dispatch, getState) {
  const state = getState();

  const response = await state.api.getTimeEntries({
    sortDir: "desc",
    sortBy: "entryDate",
    from: 0,
    size: 1
  });

  const mostRecent = get(response, "timeEntries[0]");

  dispatch({ type: actions.SET_MOST_RECENT_ENTRY, mostRecent });
};

async function _getInterlocks(dispatch, getState) {
  const state = getState();

  const payload = await state.api.getInterlocks();

  dispatch({ type: actions.FETCH_INTERLOCKS_FULFILLED, payload });
}

async function _getInitialDate(dispatch, getState) {
  const state = getState();

  const { initialDate } = await state.api.getInitialDate();

  return dispatch({ type: actions.SET_INITIAL_DATE, initialDate });
}

async function _getTotalHours(dispatch, getState) {
  const state = getState();

  const { totalHours } = await state.api.getTotalHours();

  dispatch({ type: actions.SET_TOTAL_HOURS, totalHours });
}

async function _getDefaultServiceRequest(dispatch, getState) {
  const state = getState();

  if (!state.user || !state.user.preferences || !state.user.preferences.serviceRequestPref) {
    return Promise.resolve();
  }

  return state.api.getSingleServiceRequest(state.user.preferences.serviceRequestPref)
    .then((serviceRequest) => {
      dispatch({
        type: actions.FETCH_DEFAULT_SR_FULFILLED,
        payload: serviceRequest
      });
    });
}

export function updateDefaultSelections() {
  // these promises need to be chained, because we use prefs info from
  // _getUser in _getDefaultServiceRequest
  return async (dispatch, getState) => {
    await _getUser(dispatch, getState);

    await _getDefaultServiceRequest(dispatch, getState);
  };
}

export function getUser() {
  return _getUser;
}

export function getFavorites() {
  return _getFavorites;
}

export function getInitialDate() {
  return _getInitialDate;
}

export function getTotalHours() {
  return _getTotalHours;
}

export function fetchMostRecentEntry() {
  return _fetchMostRecentEntry;
}

export function onAuthorization() {
  return async (dispatch, getState) => {
    dispatch({ type: actions.USER_DATA_LOADING });

    try {
      const user = await _getUser(dispatch, getState);

      if (!user) {
        console.error("User record unavailable");

        return;
      }

      await _getFavorites(dispatch, getState);

      await Promise.all([
        _getRoles(dispatch, getState),
        _getCategories(dispatch, getState),
        _getInterlocks(dispatch, getState),
        _getDefaultServiceRequest(dispatch, getState),
        _getInitialDate(dispatch, getState),
        _getTotalHours(dispatch, getState),
        _fetchMostRecentEntry(dispatch, getState),
      ]);

      dispatch({ type: actions.USER_DATA_LOADED });
    } catch (error) {
      console.error("something wrong in onAuthorization");

      dispatch({ type: actions.USER_DATA_LOAD_FAILED });
      dispatch({ type: actions.USER_UNAVAILABLE });

      if (error.message !== 'Unauthorized') {
        sentry.logException(error);
      }
    }
  };
};


export function addAccountFavorite(account) {
    return (dispatch, getState) => {
        getState().api.postAccountUserFavorite(account.accountId)
            .then((response) => {
                if (response.status !== 204) {
                    dispatch({type: actions.DELETE_FAVORITE_ACCOUNT, account});
                }
            })
            .catch((error) => sentry.logException(error));

        return dispatch({type: actions.ADD_FAVORITE_ACCOUNT, account});
    };
}

export function removeAccountFavorite(account) {
    return (dispatch, getState) => {
        getState().api.deleteAccountUserFavorite(account.accountId)
            .then((response) => {
                if (response.status !== 204) {
                    dispatch({type: actions.ADD_FAVORITE_ACCOUNT, account});
                }
            })
            .catch((error) => sentry.logException(error));

        return dispatch({type: actions.DELETE_FAVORITE_ACCOUNT, account});
    };
}

export function addProductGroupFavorite(productGroup) {
    return (dispatch, getState) => {
        getState().api.postProductGroupUserFavorite(productGroup.productGroupId)
            .then((response) => {
                if (response.status !== 204) {
                    dispatch({
                        type: actions.DELETE_FAVORITE_PRODUCT_GROUP,
                        productGroup
                    });
                }
            })
            .catch((error) => sentry.logException(error));

        return dispatch({
            type: actions.ADD_FAVORITE_PRODUCT_GROUP,
            productGroup
        });
    };
}

export function removeProductGroupFavorite(productGroup) {
  return (dispatch, getState) => {
    getState().api.deleteProductGroupUserFavorite(productGroup.productGroupId)
      .then((response) => {
        if (response.status !== 204) {
          dispatch({
            type: actions.ADD_FAVORITE_PRODUCT_GROUP,
            productGroup
          });
        }
      })
      .catch((error) => sentry.logException(error));

    return dispatch({
      type: actions.DELETE_FAVORITE_PRODUCT_GROUP,
      productGroup
    });
  };
}

export function addOpportunityFavorite(opportunity) {
  return (dispatch, getState) => {
    getState().api.postOpportunityUserFavorite(opportunity.opportunityId)
      .then(response => {
        if (response.status !== 204) {
          dispatch({
            type: actions.DELETE_FAVORITE_OPPORTUNITY,
            opportunity
          });
        }
      })
      .catch((error) => sentry.logException(error));

    return dispatch({
      type: actions.ADD_FAVORITE_OPPORTUNITY,
      opportunity
    });
  };
}

export function removeOpportunityFavorite(opportunity) {
  return (dispatch, getState) => {
    getState().api.deleteOpportunityUserFavorite(opportunity.opportunityId)
      .then(response => {
        if (response.status !== 204) {
          dispatch({
            type: actions.ADD_FAVORITE_OPPORTUNITY,
            opportunity
          });
        }
      })
      .catch((error) => sentry.logException(error));

    return dispatch({
      type: actions.DELETE_FAVORITE_OPPORTUNITY,
      opportunity
    });
  };
}

// These functions don't call dispatch()? Why are they here in actions? There's
// almost certainly a better place for them, but I'm not going to worry about
// it now since it's working. :/

export function getAccounts(params) {
  return (dispatch, getState) => {
    return getState().api.getAccounts(params)
      .then((response) => {
        return {
          results: _attachAccountHelpers(
            response.accounts,
            getState
          ),
          total: response.total
        };
      })
      .catch((error) => sentry.logException(error));
  };
};

export function getProductGroups(params) {
  return async (dispatch, getState) => {
    const state = getState();

    try {
      const response = await state.api.getProductGroups(params);

      const results = _attachProductGroupHelpers(response.productGroups, getState);

      return {
        results,
        total: response.total,
        filterOptions: response.aggregations.productFamilies.buckets
      };
    } catch (error) {
      sentry.logException(error);

      return {
        results: [],
        total: 0,
      }
    }
  };
};

export function getServiceRequests(params) {
  return (dispatch, getState) => {
    return getState().api.getServiceRequests(params)
      .then((response) => {
        return {
          results: response.serviceRequests,
          total: response.total
        };
      })
      .catch((error) => sentry.logException(error));
    };
};

export function getOpportunities(params) {
  return (dispatch, getState) => {
    return getState().api.getOpportunities(params)
      .then(response => {
        return {
          results: _attachOpportunityHelpers(
            response.opportunities,
            getState
          ),
          total: response.total
        };

      })
      .catch((error) => sentry.logException(error));
  };
};


// this logout function isn't used, because we need to redirect to SSO signout.
// once that page is loaded, the entire app is unloaded from the browser. no
// need to fiddle with the redux state.
export function logoutUser() {
  return (dispatch, getState) => {
    dispatch({ type: actions.LOGOUT_USER });
  };
}

export function setSettingsToggle (key, value) {
  return {
    type: actions.SET_SETTINGS_TOGGLE,
    value: Boolean(value),
    key
  };
}

export function addNotification(notification) {
  return { type: actions.ADD_NOTIFICATION, notification };
}

export function dismissNotification() {
  return { type: actions.DISMISS_NOTIFICATION };
}

/**
 * @see _attachHelpers
 * @param {object[]} accounts
 * @param {function} getState
 * @return {object[]}
 */
function _attachAccountHelpers(accounts, getState) {
  return _attachHelpers(accounts, "account", getState);
}

/**
 * @see _attachHelpers
 * @param {object[]} productGroups
 * @param {function} getState
 * @return {object[]}
 */
function _attachProductGroupHelpers (productGroups, getState) {
  return _attachHelpers(productGroups, "productGroup", getState);
}

/**
 * @see _attachHelpers
 * @param {object[]} opportunities
 * @param {function} getState
 * @return {object[]}
 */
function _attachOpportunityHelpers (opportunities, getState) {
  return _attachHelpers(opportunities, "opportunity", getState);
}

/**
 * @param {object[]} items
 * @param {string} itemType
 * @param {function} getState
 * @return {object[]}
 */
function _attachHelpers(items, itemType, getState) {
  if (!Array.isArray(items)) {
    return [];
  }

  const favoritesKey = _getFavoritesKeyFor(itemType);

  const idAttr = `${itemType}Id`;

  const helpers = {
    getId() {
      return this[idAttr];
    },
    isFavorite() {
      const favorites = getState().favorites[favoritesKey];

      const needle = { [idAttr]: this[idAttr] };

      const found = find(favorites, needle);

      return Boolean(found);
    },
  };

  return items.map((item) => {
    return Object.assign({ ...item }, helpers);
  });
}

/**
 * @param {string} itemType
 * @return {string}
 */
function _getFavoritesKeyFor(itemType) {
  switch (itemType) {
  case "account":
  case "accounts":
    return "accounts";
  case "opportunity":
  case "opportunities":
    return "opportunities";
  case "productGroup":
  case "productGroups":
    return "productGroups";
  default:
    throw new Error(`Unknown favorite key: ${itemType}`);
  }
}

/**
 * @param {?object} user
 * @return {boolean}
 */
function isUserUnavailable(user) {
  if (user == null) {
    return true;
  }

  if (typeof user.message === "string" && !!user.message) {
    if (user.message.startsWith("No user found")) {
      return true;
    } else if (user.message === "Unauthorized") {
      return true;
    }
  }

  return false;
}



// WEBPACK FOOTER //
// ./app/actions/userActions.js